<script setup lang="ts">
import { computed, type PropType } from 'vue'
import {
    type PriceComponent,
    PriceComponentType
} from '../../../../interfaces/simulation'

const props = defineProps({
    item: {
        type: Object as PropType<PriceComponent>,
        required: true
    },
    totalPrice: {
        type: Number,
        required: true
    },
    index: {
        type: Number,
        required: true
    },
    length: {
        type: Number,
        required: true
    },
    isDisabled: {
        type: Boolean,
        required: true
    },
    isBoost: {
        type: Boolean,
        required: true
    },
    hasMarginItem: {
        type: Boolean,
        required: true
    }
})

const chartBarHeightPercentage = computed(() => {
    const componentPrice = parseFloat(props.item.price.amount) / 100

    return (componentPrice / props.totalPrice) * 100
})

const chartBarBackground = computed(() => {
    switch (props.item.type) {
        case PriceComponentType.SESSION:
            return 'bg-indigo-800'
        case PriceComponentType.ENERGY:
        case PriceComponentType.FLAT:
        case PriceComponentType.PARKING_TIME:
            return 'bg-purple-400'
        case PriceComponentType.CHARGE_TIME:
            return 'bg-violet-500'
        case PriceComponentType.MARGIN:
        default:
            return 'bg-branding-500'
    }
})

const textColorClass = computed(() => {
    switch (props.item.type) {
        case PriceComponentType.SESSION:
            return 'text-indigo-800'
        case PriceComponentType.ENERGY:
        case PriceComponentType.FLAT:
        case PriceComponentType.PARKING_TIME:
            return 'text-purple-700'
        case PriceComponentType.CHARGE_TIME:
            return 'text-violet-800'
        case PriceComponentType.MARGIN:
        default:
            return 'text-sky-600'
    }
})

const chartBarClasses = computed(() => {
    const isFirst = props.index === 0

    const isLast =
        props.index ===
        (props.hasMarginItem ? props.length - 2 : props.length - 1)

    let borderRadiusClasses = ''

    const baseClasses = props.isDisabled
        ? 'bg-gray-disabled'
        : chartBarBackground.value

    if (props.length === 1) {
        borderRadiusClasses = 'rounded 425:rounded-md'
    } else if (props.item.type === PriceComponentType.MARGIN) {
        borderRadiusClasses = 'rounded 425:rounded-b-md 425:rounded-t'
    } else if (isFirst) {
        borderRadiusClasses =
            props.length === 2 && props.hasMarginItem
                ? 'rounded 425:rounded-md'
                : 'rounded-t 425:rounded-md'
    } else if (isLast) {
        borderRadiusClasses = 'rounded-b 425:rounded-md'
    } else {
        borderRadiusClasses = '425:rounded-md'
    }

    return `${baseClasses} ${borderRadiusClasses}`
})

const grayTextColorClass = computed(() => {
    if (props.item.type !== PriceComponentType.MARGIN) {
        return 'text-gray-disabled'
    }

    return props.isDisabled ? 'text-gray-disabled' : 'text-gray-l-500'
})
</script>

<template>
    <div
        class="w-full flex items-center justify-center gap-3 425:gap-4 duration-300"
        :class="[
            index < length - 1 && 'mb-[1px] 425:mb-[2px]',
            item.type === PriceComponentType.MARGIN && 'mt-1 425:mt-[2px]'
        ]"
        :style="{
            height: `${chartBarHeightPercentage}%`
        }"
    >
        <div class="h-full w-20 self-stretch" :class="chartBarClasses" />

        <div
            class="flex gap-1 items-center justify-between w-full my-1 tracking-[-0.13px] 425:tracking-[-0.17px]"
        >
            <span
                class="font-medium text-[13px] 425:text-[17px] leading-4 425:leading-5"
                :class="isDisabled ? 'text-gray-disabled' : textColorClass"
            >
                {{ $t(`simulation.charge.cost_details.label.${item.type}`) }}
            </span>

            <div class="flex items-center gap-1">
                <span
                    data-testid="price"
                    class="leading-4 425:leading-5"
                    :class="[
                        item.type === PriceComponentType.MARGIN && isBoost
                            ? 'font-normal line-through text-[11px] 425:text-[15px]'
                            : 'font-semibold text-[13px] 425:text-[17px]',
                        isDisabled ||
                        (item.type === PriceComponentType.MARGIN && isBoost)
                            ? grayTextColorClass
                            : textColorClass
                    ]"
                >
                    {{ $n(parseFloat(item.price.amount) / 100, 'currency') }}
                </span>

                <div
                    v-if="item.type === PriceComponentType.MARGIN && isBoost"
                    class="flex gap-1 items-center"
                >
                    <span
                        data-testid="boosted-price"
                        class="font-semibold text-[13px] 425:text-[17px] leading-4 425:leading-5"
                        :class="
                            isDisabled ? 'text-gray-disabled' : 'text-sky-600'
                        "
                    >
                        {{ $n(0, 'currency', { minimumFractionDigits: 0 }) }}
                    </span>
                    <IconRocketLightning
                        is-solid
                        class="size-5"
                        :class="
                            isDisabled
                                ? 'text-gray-disabled'
                                : 'text-icon-secondary-accent'
                        "
                    />
                </div>
            </div>
        </div>
    </div>
</template>
